import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LanguageContext = createContext(null);

// Minimal English fallback so UI never renders blank while loading
const FALLBACK = {};

export const LanguageProvider = ({ children }) => {
  const [languages, setLanguages] = useState([]);
  const [lang, setLangState] = useState("en");
  const [strings, setStrings] = useState(FALLBACK);
  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState(false);

  const detectInitial = useCallback((available) => {
    const saved = localStorage.getItem("macas_lang");
    if (saved && available.some((l) => l.code === saved)) return saved;
    const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
    if (available.some((l) => l.code === nav)) return nav;
    return "en";
  }, []);

  const loadStrings = useCallback(async (code) => {
    setLoading(true);
    try {
      const cacheKey = `macas_i18n_${code}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        setStrings(JSON.parse(cached));
        setLoading(false);
        setTranslating(false);
      } else {
        // No cache yet: this may hit the AI translator and take a few seconds
        setTranslating(code !== "en");
      }
      const res = await axios.get(`${API}/i18n`, { params: { lang: code }, timeout: 60000 });
      setStrings(res.data.strings);
      localStorage.setItem(cacheKey, JSON.stringify(res.data.strings));
    } catch (e) {
      console.error("i18n load failed", e);
    } finally {
      setLoading(false);
      setTranslating(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/languages`);
        setLanguages(res.data.languages);
        const initial = detectInitial(res.data.languages);
        setLangState(initial);
        document.documentElement.lang = initial;
        await loadStrings(initial);
      } catch (e) {
        console.error(e);
        setLoading(false);
      }
    })();
  }, [detectInitial, loadStrings]);

  const setLang = useCallback((code) => {
    setLangState(code);
    localStorage.setItem("macas_lang", code);
    document.documentElement.lang = code;
    loadStrings(code);
  }, [loadStrings]);

  const t = useCallback((key) => strings[key] || key, [strings]);

  return (
    <LanguageContext.Provider value={{ languages, lang, setLang, t, loading, translating }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
